import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getMsalInstance, scopes, logoutFromSSO } from '../utils/msal';

const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID;

// Guard to prevent concurrent interactive API calls
let flowInProgress = false;

const SSOLogin = () => {
  const { loginWithSSO } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('initializing');
  const [error, setError] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const hasRunRef = useRef(false);

  const navigateByRole = useCallback((user) => {
    if (user.role === 'SuperAdmin' || user.role === 'Sales') {
      navigate('/admin');
    } else if (['Admin', 'Manager'].includes(user.role)) {
      navigate('/manufacturer');
    } else if (user.role === 'Dealer') {
      navigate('/dealer');
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (!clientId) {
      setError('SSO is not configured. Please contact your administrator.');
      setStatus('error');
      return;
    }

    // Prevent double execution from React Strict Mode
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const runFlow = async () => {
      if (flowInProgress) return;
      flowInProgress = true;

      try {
        const msalInstance = await getMsalInstance();

        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          setStatus('authenticating');
          const idToken = response.idToken;

          if (!idToken) {
            setError('No ID token received from Microsoft.');
            setStatus('error');
            return;
          }

          const result = await loginWithSSO(idToken);

          if (result.success) {
            if (result.user.role === 'Dealer' && result.user.password_tmp === 'YES') {
              navigate('/dealer');
            } else {
              navigateByRole(result.user);
            }
          } else if (result.code === 'SSO_USER_NOT_REGISTERED') {
            setSsoEmail(result.email || '');
            setStatus('user_not_registered');
          } else {
            setError(result.error || 'SSO login failed');
            setStatus('error');
          }
        } else {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            setStatus('authenticating');
            const silentResult = await msalInstance.acquireTokenSilent({
              scopes,
              account: accounts[0]
            }).catch(() => null);

            if (silentResult?.idToken) {
              const result = await loginWithSSO(silentResult.idToken);
              if (result.success) {
                navigateByRole(result.user);
              } else if (result.code === 'SSO_USER_NOT_REGISTERED') {
                setSsoEmail(result.email || '');
                setStatus('user_not_registered');
              } else {
                setError(result.error || 'SSO login failed');
                setStatus('error');
              }
            } else {
              msalInstance.loginRedirect({ scopes });
            }
          } else {
            setStatus('redirecting');
            msalInstance.loginRedirect({ scopes });
          }
        }
      } catch (err) {
        console.error('SSO flow error:', err);
        setError(err.message || 'An error occurred during sign in.');
        setStatus('error');
      } finally {
        flowInProgress = false;
      }
    };

    runFlow();
  }, [loginWithSSO, navigate, navigateByRole]);

  if (status === 'user_not_registered') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Sign in successful
          </Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            You have successfully signed in with Microsoft Entra ID.
          </Alert>
          {ssoEmail && (
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Signed in as: {ssoEmail}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your account is not yet registered in the SMOD platform.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please contact an administrator to add your user account.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={async () => {
              const redirected = await logoutFromSSO();
              if (!redirected) navigate('/login');
            }}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Sign in failed
          </Typography>
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={async () => {
              const redirected = await logoutFromSSO();
              if (!redirected) navigate('/login');
            }}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1">
            {status === 'redirecting' ? 'Redirecting to sign in...' : 'Signing you in...'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SSOLogin;
